import { ENTITY_MAP, entitiesForSheet, getField, type EntityName, type FieldDef } from "./schema";
import { isBlank, normalizeField } from "./normalize";
import { withDefaults, type ImportSettings } from "./types";

export interface FieldRef {
  entity: EntityName;
  field: string;
}

export interface ColumnMapping {
  sourceIndex: number;
  header: string;
  target: FieldRef | null;
  confidence: number;
  reason: string;
  alternatives: { target: FieldRef; confidence: number }[];
}

/** Editable mapping object: the UI (or the AI layer) changes `columns[i].target`. */
export interface Mapping {
  /** Primary entity of the sheet. "members" means a wide sheet that may also carry plan / due / PT / device columns. */
  entity: EntityName;
  columns: ColumnMapping[];
}

export interface SuggestInput {
  headers: string[];
  sampleRows: string[][];
  entity: EntityName;
}

/** The AI layer plugs in here later and must return the same shape. */
export interface MappingSuggester {
  suggest(input: SuggestInput): Mapping | Promise<Mapping>;
}

export interface SuggestOptions {
  entity?: EntityName;
  settings?: Partial<ImportSettings>;
  /** Columns scoring below this stay unmapped. */
  minConfidence?: number;
}

export function normHeader(h: string): string {
  return h.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

function levenshtein(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...new Array<number>(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) dp[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i]![j] = Math.min(dp[i - 1]![j]! + 1, dp[i]![j - 1]! + 1, dp[i - 1]![j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
  }
  return dp[a.length]![b.length]!;
}

function scoreHeader(header: string, f: FieldDef): { score: number; reason: string } {
  const h = normHeader(header);
  if (h === "") return { score: 0, reason: "empty header" };
  const hTokens = h.split(" ");
  // "Balance Sessions" counts sessions, not rupees; it must not become a due.
  if (f.type === "paise" && hTokens.some((t) => t === "session" || t === "sessions")) return { score: 0, reason: "header counts sessions" };
  let best = { score: 0, reason: "no match" };
  const consider = (score: number, reason: string) => { if (score > best.score) best = { score, reason }; };
  for (const syn of [...f.synonyms, normHeader(f.label), f.name.replace(/_/g, " ")]) {
    if (h === syn) { consider(0.95, `header equals "${syn}"`); continue; }
    const sTokens = syn.split(" ");
    if (sTokens.every((t) => hTokens.includes(t))) consider(0.6 + 0.25 * (sTokens.length / hTokens.length), `header contains "${syn}"`);
    else if (syn.length >= 5 && h.length >= 5 && levenshtein(h, syn) <= Math.max(1, Math.floor(syn.length * 0.15))) consider(0.7, `header is close to "${syn}"`);
  }
  return best;
}

function sampleCompat(f: FieldDef, samples: string[], s: ImportSettings): number | null {
  const vals = samples.filter((v) => !isBlank(v, s));
  if (vals.length === 0 || f.type === "string") return null;
  const ok = vals.filter((v) => normalizeField(f, v, s).issues.every((i) => i.severity !== "error")).length;
  return ok / vals.length;
}

interface Pair { col: number; target: FieldRef; score: number; reason: string; order: number }

export function suggestMapping(headers: string[], sampleRows: string[][], options: SuggestOptions = {}): Mapping {
  const entity = options.entity ?? "members";
  const s = withDefaults(options.settings);
  const min = options.minConfidence ?? 0.55;
  const candidates = entitiesForSheet(entity).flatMap((e, ei) =>
    ENTITY_MAP[e].fields
      // On a wide sheet member_phone is inherited from members.phone; a second phone column (Alt Mobile) must not become the link.
      .filter((f) => !(entity === "members" && e !== "members" && f.name === "member_phone"))
      .map((f, fi) => ({ ref: { entity: e, field: f.name } as FieldRef, f, order: ei * 100 + fi })),
  );

  const pairs: Pair[] = [];
  headers.forEach((header, col) => {
    const samples = sampleRows.map((r) => r[col] ?? "");
    for (const c of candidates) {
      let { score, reason } = scoreHeader(header, c.f);
      const compat = sampleCompat(c.f, samples, s);
      if (score > 0 && compat !== null) {
        if (compat < 0.5) { score *= 0.5; reason += `; but only ${Math.round(compat * 100)}% of sample values fit`; }
        else if (compat >= 0.8 && c.f.type !== "enum") score = Math.min(0.99, score + 0.03);
      }
      if (score === 0 && (c.f.type === "phone" || c.f.type === "email") && compat !== null && compat >= 0.8 && samples.filter((v) => !isBlank(v, s)).length >= 2) {
        score = 0.56;
        reason = `sample values all look like ${c.f.type === "phone" ? "phone numbers" : "emails"}`;
      }
      if (score >= min) pairs.push({ col, target: c.ref, score, reason, order: c.order });
    }
  });

  pairs.sort((a, b) => b.score - a.score || a.order - b.order || a.col - b.col);
  const usedCols = new Set<number>();
  const usedTargets = new Set<string>();
  const chosen = new Map<number, Pair>();
  for (const p of pairs) {
    const key = `${p.target.entity}.${p.target.field}`;
    if (usedCols.has(p.col) || usedTargets.has(key)) continue;
    usedCols.add(p.col); usedTargets.add(key); chosen.set(p.col, p);
  }

  const columns: ColumnMapping[] = headers.map((header, col) => {
    const pick = chosen.get(col);
    const alternatives = pairs
      .filter((p) => p.col === col && p !== pick)
      .slice(0, 2)
      .map((p) => ({ target: p.target, confidence: round2(p.score) }));
    return pick
      ? { sourceIndex: col, header, target: pick.target, confidence: round2(pick.score), reason: pick.reason, alternatives }
      : { sourceIndex: col, header, target: null, confidence: 0, reason: "no confident match", alternatives };
  });
  return { entity, columns };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function heuristicSuggester(settings?: Partial<ImportSettings>): MappingSuggester {
  return { suggest: (i) => suggestMapping(i.headers, i.sampleRows, { entity: i.entity, ...(settings ? { settings } : {}) }) };
}

/** Returns a new mapping with one column re-targeted (confidence 1: a human decided). */
export function setTarget(m: Mapping, sourceIndex: number, target: FieldRef | null): Mapping {
  return {
    ...m,
    columns: m.columns.map((c) =>
      c.sourceIndex === sourceIndex ? { ...c, target, confidence: target ? 1 : 0, reason: target ? "set by user" : "cleared by user" } : c,
    ),
  };
}

export interface MappingCheck {
  ok: boolean;
  missingRequired: FieldRef[];
  duplicateTargets: FieldRef[];
  unknownTargets: FieldRef[];
  /** Wide-sheet columns whose entity has none of its required fields mapped (e.g. a lone Trainer column); they are ignored, not errors. */
  inertTargets: FieldRef[];
}

/** Required fields for every entity that has at least one mapped column. `member_phone` is inherited from `members.phone` on wide sheets. */
export function checkMapping(m: Mapping): MappingCheck {
  const mapped = m.columns.filter((c) => c.target).map((c) => c.target as FieldRef);
  const seen = new Set<string>();
  const duplicateTargets: FieldRef[] = [];
  const unknownTargets: FieldRef[] = [];
  for (const t of mapped) {
    const k = `${t.entity}.${t.field}`;
    if (!getField(t.entity, t.field)) unknownTargets.push(t);
    if (seen.has(k)) duplicateTargets.push(t);
    seen.add(k);
  }
  const entities = new Set(mapped.map((t) => t.entity));
  if (m.entity === "members") entities.add("members");
  else entities.add(m.entity);
  const missingRequired: FieldRef[] = [];
  const inertTargets: FieldRef[] = [];
  for (const e of entities) {
    if (m.entity === "members" && e !== "members") {
      const anchored = ENTITY_MAP[e].fields.some((f) => f.required && f.name !== "member_phone" && seen.has(`${e}.${f.name}`));
      if (!anchored) { inertTargets.push(...mapped.filter((t) => t.entity === e)); continue; }
    }
    for (const f of ENTITY_MAP[e].fields) {
      if (!f.required || seen.has(`${e}.${f.name}`)) continue;
      if (f.name === "member_phone" && seen.has("members.phone")) continue;
      missingRequired.push({ entity: e, field: f.name });
    }
  }
  return { ok: !missingRequired.length && !duplicateTargets.length && !unknownTargets.length, missingRequired, duplicateTargets, unknownTargets, inertTargets };
}
